import { cn } from "@/lib/cn";
import { useNavigateWithBack } from "@/lib/navBack";

/**
 * "Smart links" - anywhere we display a customer name, dispatch number, or
 * tech name, use these components so the click behavior is consistent.
 *
 * Today they all navigate to placeholder pages.
 * As we build out Customer 360, dispatch detail, tech detail, these
 * automatically start working - all the wiring is already here.
 */

interface CustomerLinkProps {
  custno: string;
  locno?: string | null;
  /** Land on a specific 360 tab (e.g. \"agreements\" for renewal rows). */
  tab?: string;
  children: React.ReactNode;
  className?: string;
  /** Hover hint — e.g. the "Qualify" buttons explain themselves to new hires. */
  title?: string;
}

export function CustomerLink({
  custno,
  locno,
  tab,
  children,
  className,
  title,
}: CustomerLinkProps) {
  const navigate = useNavigateWithBack();
  return (
    <button
      type="button"
      title={title}
      onClick={(e) => {
        e.stopPropagation();
        // Deep-link to a specific location when one is given (e.g. the
        // property-history card lands you on the SHARED address of the other
        // account, not their primary location). The 360 honors ?loc and falls
        // back to the primary location when it's absent.
        const params = new URLSearchParams();
        if (locno) params.set("loc", locno);
        if (tab) params.set("tab", tab);
        const qs = params.toString();
        navigate(`/customers/${custno}${qs ? `?${qs}` : ""}`);
      }}
      className={cn(
        "text-left font-medium text-slate-900 underline-offset-2 hover:text-brand-700 hover:underline",
        className,
      )}
    >
      {children}
    </button>
  );
}

interface DispatchLinkProps {
  dispatch: string;
  className?: string;
}

export function DispatchLink({ dispatch, className }: DispatchLinkProps) {
  const navigate = useNavigateWithBack();
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        // Navigates direct to the dispatch detail; useNavigateWithBack records
        // the screen we're leaving so "Back" returns there (NAV-03), not a
        // hardcoded board default.
        navigate(`/dispatch/${dispatch}`);
      }}
      className={cn(
        "rounded bg-slate-100 px-2 py-0.5 font-mono text-xs text-slate-700 transition-colors hover:bg-brand-50 hover:text-brand-700",
        className,
      )}
    >
      {dispatch}
    </button>
  );
}

interface TechLinkProps {
  empno: string;
  name: string;
  className?: string;
}

export function TechLink({ empno, name, className }: TechLinkProps) {
  const navigate = useNavigateWithBack();
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        navigate(`/board?tech=${empno}`);
      }}
      className={cn(
        "text-slate-600 underline-offset-2 hover:text-brand-700 hover:underline",
        className,
      )}
    >
      {name}
    </button>
  );
}
